import type { AccessPrivilege } from '@stopal/shared/access-privilege'
import { type ComponentType, createElement, lazy } from 'react'
import type { NexusIconName } from '@/components/nexus/shell/icon'

/**
 * Props passed to a tab component, parsed from the tab's URL.
 * Concrete tabs narrow this with their own typed props.
 */
export type TabProps = Record<string, string | undefined>

interface BaseTabDef<P extends TabProps> {
  /** Unique tab type identifier (e.g. 'orders', 'pallets'). */
  name: string
  /** Lucide icon name, resolved at render time. */
  icon: NexusIconName
  /** Permission required to open this tab. Omit for unrestricted (still inside RequireRole work). */
  permission?: AccessPrivilege
  /**
   * Role required to open this tab, for tabs gated on identity rather than on
   * a privilege — the same axis `Section` carries. Separate from `permission`
   * because `hasGate` resolves that one against the caller's PRIVILEGE list: a
   * role name written there passes only by not matching any privilege, and
   * would silently open the moment a privilege of that name were seeded.
   *
   * **Moving a tab from `permission: 'superadmin'` to `role: 'superadmin'` is
   * not a rename, and it is not safe in bulk.** A tile's own `gate` shadows
   * `permission`; it does NOT shadow `role`, which `canListTab` checks first.
   * So on any tile carrying its own gate, `permission: 'superadmin'` is an
   * inert mislabel that the gate is overriding, and converting it turns that
   * mislabel into an enforced gate — removing the tile from exactly the
   * privilege holders it was shadowing for.
   *
   * A bulk conversion would hide `maestro.documents`, `admin.users` and
   * `admin.maestro-settings` from employees holding `access_documents` /
   * `access_users` / `access_maestro_settings`. The mislabel is at ZERO and
   * `sections.test.ts` holds it there unconditionally.
   *
   * **A sweep must not change who sees what**, and the three privileges do
   * not gate alike: `access_users` gates the Users tab and `usersProcedure`;
   * `access_documents` gates the Documents tabs while `documents.router` stays
   * `maestroProcedure` (server-side it is enforced only by `studioProcedure`,
   * for Studio); `access_maestro_settings` is seeded and read by no section,
   * tab or procedure.
   *
   * **The axis is load-bearing**, not declared-and-unread. Ten tab defs
   * carry `role`, and seven of them sit on a tile with no `gate`, so the
   * branch below is the only thing in `canListTab` refusing them. For five of
   * those the Dev section's own `role: 'superadmin'` would refuse them anyway;
   * for `notifications` and `maestro-settings` the section is privilege-gated,
   * so this branch is the whole gate. Delete or reorder it and every
   * `access_admin` holder is shown a door the page's `adminProcedure`
   * refuses. `sections.test.ts` guards this.
   *
   * **A tab with no tile is gated by `permission` ALONE.** `CREATE_ACTIONS`
   * (`shell/search-results.tsx`) calls `canListTab(def, undefined, …)`, so
   * there is nothing to shadow it and `hasGate(undefined, …)` returns true —
   * dropping the field there offers the shortcut to everyone.
   */
  role?: string
  /** Static or dynamic label. Receives parsed props. */
  label: (props: P) => string
  /** Build a URL from props (used to update history when this tab is focused). */
  urlPath: (props: P) => string
  /** Match a pathname; return parsed props or null. */
  match: (pathname: string, search: URLSearchParams) => P | null
  /**
   * Prop keys that are view state rather than identity. Excluded from the tab's
   * identity key so e.g. `?from=task1` and `?from=task2` focus the same tab
   * instance instead of opening a new one, and stripped before the tab is
   * persisted.
   *
   * Usually a query param, but the rule is keyed on the prop NAME, not on where
   * its value sits in the URL: Marketing lists its `/ops/marketing/<section>`
   * path segment here, so all of its tabs are one workspace tab.
   */
  searchKeys?: readonly string[]
}

/**
 * Shape passed to {@link registerTab} — typed in the tab's narrow props P
 * so the lazy import type-checks against the component's actual signature
 * at the registration site.
 */
export interface TabDefInput<P extends TabProps> extends BaseTabDef<P> {
  component: () => Promise<{ default: ComponentType<P> }>
}

/**
 * Stored shape — component widened to `ComponentType<TabProps>` via a small
 * wrapper so heterogeneous tabs share a single `Map`. The variance hop
 * happens once inside {@link registerTab}; consumers (tab-pane, sidebar,
 * shell) treat every tab uniformly.
 */
export interface TabDef extends BaseTabDef<TabProps> {
  Component: ComponentType<TabProps>
}

const REGISTRY = new Map<string, TabDef>()

export function registerTab<P extends TabProps>(def: TabDefInput<P>): void {
  // Each tab's component expects its own narrow P, but the registry stores
  // a uniform `ComponentType<TabProps>`. Wrap the lazy component once so the
  // unsafe narrowing cast is localised here — TS can't see that match()
  // always populates the keys the component reads, but the runtime guarantee
  // is exactly that.
  const Lazy = lazy(def.component)
  const Component: ComponentType<TabProps> = (props) =>
    createElement(Lazy, props as unknown as P)
  // label / urlPath / match are typed in P at the input site but stored as
  // TabProps consumers; the cast widens the input variance once at the
  // boundary (same pattern the existing as-unknown-as on registerTab
  // localised before).
  const { component: _component, ...rest } = def
  REGISTRY.set(def.name, {
    ...(rest as unknown as BaseTabDef<TabProps>),
    Component,
  })
}

export function getTab(name: string): TabDef | undefined {
  return REGISTRY.get(name)
}

export function listTabs(): TabDef[] {
  return Array.from(REGISTRY.values())
}

/**
 * Resolve an /ops pathname to a tab + props.
 * Returns null for `/ops` (launcher) or unmatched paths.
 */
export function resolveUrl(
  pathname: string,
  search: URLSearchParams,
): { tab: TabDef; props: TabProps } | null {
  // Strip leading /ops so per-tab matchers can stay short.
  const stripped = pathname.replace(/^\/ops\/?/, '/')
  if (stripped === '/' || stripped === '') return null
  for (const tab of REGISTRY.values()) {
    const props = tab.match(stripped, search)
    if (props) return { tab, props }
  }
  return null
}
